import React, { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { adminApi } from "./adminApi";
import DebugSyncModal from "./DebugSyncModal";

const card = { background: "white", borderRadius: 10, padding: "14px 16px", border: "1px solid #E5E7EB" };
const cardTone = {
  green:  { bg: "#DCFCE7", fg: "#166534" },
  amber:  { bg: "#FEF3C7", fg: "#92400E" },
  red:    { bg: "#FEE2E2", fg: "#B91C1C" },
  orange: { bg: "#FFEDD5", fg: "#9A3412" },
};
const th = { padding: "10px 14px", fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1, textAlign: "left", borderBottom: "1px solid #E5E7EB", fontWeight: 700 };
const td = { padding: "10px 14px", fontSize: 12.5, color: "#0F1B2D", borderTop: "1px solid #F1F5F9", verticalAlign: "top" };

function ago(iso) {
  if (!iso) return "Never";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function StatusBadge({ kind }) {
  const map = {
    healthy:  { bg: "#DCFCE7", fg: "#166534", label: "Healthy" },
    stale:    { bg: "#FEF3C7", fg: "#92400E", label: "Stale" },
    never:    { bg: "#FEE2E2", fg: "#B91C1C", label: "Never Synced" },
    outdated: { bg: "#FFEDD5", fg: "#9A3412", label: "Outdated" },
  };
  const m = map[kind] || { bg: "#F3F4F6", fg: "#374151", label: kind };
  return <span style={{ background: m.bg, color: m.fg, padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{m.label}</span>;
}

function LogsModal({ customer, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!customer?.id) return;
    adminApi.tallyLogs(customer.id).then((r) => setRows(r.ok ? r.data?.logs || [] : []));
  }, [customer?.id]);
  if (!customer) return null;
  return (
    <div
      data-testid="tally-logs-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(15,27,45,0.7)", zIndex: 70, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24 }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: "white", width: "100%", maxWidth: 800, borderRadius: 12, overflow: "hidden", marginTop: 40 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #E5E7EB", background: "#F9FAFB" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0F1B2D" }}>Tally sync logs · {customer.name}</div>
            <div style={{ fontSize: 12, color: "#6B7280" }}>{customer.company_name}</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#6B7280" }}><X size={18} /></button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={th}>Sync at</th><th style={th}>Status</th><th style={th}>Records</th><th style={th}>Duration</th><th style={th}>Agent v</th><th style={th}>Error</th>
            </tr></thead>
            <tbody>
              {rows == null && <tr><td style={{ ...td, textAlign: "center" }} colSpan={6}>Loading…</td></tr>}
              {rows && rows.length === 0 && <tr><td style={{ ...td, textAlign: "center", color: "#9CA3AF" }} colSpan={6}>No syncs yet.</td></tr>}
              {rows && rows.map((r, i) => (
                <tr key={i}>
                  <td style={td}>{ago(r.sync_at)}</td>
                  <td style={td}><StatusBadge kind={r.status === "success" ? "healthy" : "stale"} /></td>
                  <td style={td}>{r.records_synced ?? "—"}</td>
                  <td style={td}>{r.duration_ms ? `${r.duration_ms}ms` : "—"}</td>
                  <td style={td}>{r.agent_version || "—"}</td>
                  <td style={{ ...td, color: "#B91C1C" }}>{r.error_message || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function TallyHealthMonitor({ customers = [], onOpenChecklist }) {
  const [health, setHealth] = useState(null);
  const [statusMap, setStatusMap] = useState({});
  const [logsCustomer, setLogsCustomer] = useState(null);
  const [debugCustomer, setDebugCustomer] = useState(null);

  const refresh = useCallback(async () => {
    const h = await adminApi.tallyHealth();
    if (h.ok) setHealth(h.data);
    // 2026-09 RCA fix: was up to 50 individual /tally/status/{id} calls via
    // Promise.all — "parallel" on the frontend, but the backend's single
    // worker processed them sequentially (up to 250 blocking Supabase calls
    // every 60s), confirmed as the dominant cause of the production
    // brownout. One batched call now does the same work in 5 Supabase calls.
    const sample = customers.slice(0, 50);
    if (sample.length === 0) { setStatusMap({}); return; }
    const r = await adminApi.tallyStatusBatch(sample.map((c) => c.id));
    setStatusMap(r.ok ? (r.data?.statuses || {}) : {});
  }, [customers]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  const kindOf = (cust) => {
    const s = statusMap[cust.id];
    if (!s || !s.has_config) return "never";
    if (s.is_outdated) return "outdated";
    if (s.last_seen) {
      const ageMin = (Date.now() - new Date(s.last_seen).getTime()) / 60000;
      if (ageMin <= 90) return "healthy";
    }
    // Has a config but no recent sync — stale during business hours, otherwise still flag as stale
    return "stale";
  };

  const cardCfg = [
    { id: "healthy",  label: "Healthy",        value: health?.syncing_healthy ?? "—", tone: "green" },
    { id: "stale",    label: "Stale",          value: health?.stale ?? "—",           tone: "amber" },
    { id: "never",    label: "Never Synced",   value: health?.never_synced ?? "—",    tone: "red" },
    { id: "outdated", label: "Outdated Agent", value: health?.outdated_agents ?? "—", tone: "orange" },
    { id: "online",   label: "Agent Online",   value: health?.agent_online ?? "—",    tone: "green" },
    { id: "offline",  label: "Agent Offline",  value: health?.agent_offline ?? "—",   tone: "red" },
    { id: "errors",   label: "Errors (24h)",   value: health?.errors_24h ?? "—",      tone: "amber" },
  ];

  const errKindLabel = (kind) => {
    const map = {
      tally_unreachable:      "Tally not reachable",
      tally_garbled_response: "Garbled Tally response",
      xml_parse_failed:       "XML parse failed",
      version_detect_failed:  "Version detect failed",
      push_failed:            "Push to C-ORE failed",
      auth_failed:            "Auth failed",
      scheduler_crash:        "Scheduler crashed",
      config_invalid:         "Config invalid",
      failed:                 "Sync failed",
    };
    return map[kind] || (kind || "Error");
  };

  return (
    <div data-testid="tally-health-monitor" style={{ marginTop: 24 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: "#1E3A5F", marginBottom: 12 }}>
        Tally Sync Health Monitor
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        {cardCfg.map((c) => {
          const tone = cardTone[c.tone] || cardTone.green;
          return (
            <div key={c.id} data-testid={`tally-card-${c.id}`} style={{ ...card, background: tone.bg, borderColor: tone.fg + "33" }}>
              <div style={{ fontSize: 11, color: tone.fg, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>{c.label}</div>
              <div style={{ fontSize: 26, color: tone.fg, fontWeight: 800, marginTop: 4 }}>{c.value}</div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 16, background: "white", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #E5E7EB", fontSize: 14, fontWeight: 700, color: "#0F1B2D" }}>
          Customer Tally Status
          <span style={{ color: "#6B7280", fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            (refreshes every 60s · showing up to 50 customers)
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table data-testid="tally-status-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={th}>Customer</th>
              <th style={th}>Last Sync</th>
              <th style={th}>Heartbeat</th>
              <th style={th}>Syncs Today</th>
              <th style={th}>Agent</th>
              <th style={th}>Status</th>
              <th style={th}>Last Error</th>
              <th style={th}>Action</th>
            </tr></thead>
            <tbody>
              {customers.length === 0 && (
                <tr><td colSpan={8} style={{ ...td, textAlign: "center", color: "#9CA3AF", padding: 24 }}>No customers yet.</td></tr>
              )}
              {customers.slice(0, 50).map((c) => {
                const s = statusMap[c.id];
                const kind = kindOf(c);
                const hb = s?.last_heartbeat_at;
                const hbAgeMin = hb ? Math.floor((Date.now() - new Date(hb).getTime()) / 60000) : null;
                const hbOnline = hbAgeMin !== null && hbAgeMin <= 15;
                const err = s?.last_error;
                return (
                  <tr key={c.id} data-testid={`tally-row-${c.id}`}>
                    <td style={td}>
                      <div style={{ fontWeight: 700 }}>{c.name}</div>
                      <div style={{ color: "#6B7280" }}>{c.company_name || "—"}</div>
                    </td>
                    <td style={td}>{ago(s?.last_seen)}</td>
                    <td style={td} data-testid={`tally-heartbeat-${c.id}`}>
                      {hb ? (
                        <span style={{
                          background: hbOnline ? "#DCFCE7" : "#FEE2E2",
                          color:      hbOnline ? "#166534" : "#B91C1C",
                          padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                        }}>
                          {hbOnline ? "Online" : "Offline"} · {ago(hb)}
                        </span>
                      ) : (
                        <span style={{ color: "#9CA3AF" }}>—</span>
                      )}
                    </td>
                    <td style={td}>{s?.sync_count_today ?? 0}</td>
                    <td style={td}>{s?.agent_version || "Not Installed"}</td>
                    <td style={td}><StatusBadge kind={kind} /></td>
                    <td style={td} data-testid={`tally-lasterr-${c.id}`}>
                      {err ? (
                        <div title={err.message || ""} style={{ maxWidth: 220 }}>
                          <div style={{
                            background: "#FEE2E2", color: "#B91C1C",
                            padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                            display: "inline-block",
                          }}>
                            {errKindLabel(err.kind)}
                          </div>
                          <div style={{
                            color: "#6B7280", fontSize: 11, marginTop: 4,
                            overflow: "hidden", textOverflow: "ellipsis",
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                          }}>
                            {err.message || "—"}
                          </div>
                          <div style={{ color: "#9CA3AF", fontSize: 10, marginTop: 2 }}>
                            {ago(err.at)} · via {err.source === "agent_error" ? "agent" : "sync log"}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: "#16A34A", fontSize: 11, fontWeight: 600 }}>None</span>
                      )}
                    </td>
                    <td style={td}>
                      {s?.has_config ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <button
                            data-testid={`tally-logs-${c.id}`}
                            onClick={() => setLogsCustomer(c)}
                            style={{ background: "white", border: "1px solid #D1D5DB", color: "#374151", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                          >
                            View Logs
                          </button>
                          <button
                            data-testid={`tally-debug-${c.id}`}
                            onClick={() => setDebugCustomer(c)}
                            title="Run a one-off diagnostic XML request against this customer's Tally"
                            style={{ background: "#0F172A", border: "none", color: "white", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                          >
                            Debug Sync
                          </button>
                        </div>
                      ) : (
                        <button
                          data-testid={`tally-setup-${c.id}`}
                          onClick={() => onOpenChecklist?.(c)}
                          style={{ background: "#1D4ED8", color: "white", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                        >
                          Setup Tally
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {logsCustomer && <LogsModal customer={logsCustomer} onClose={() => setLogsCustomer(null)} />}
      {debugCustomer && <DebugSyncModal customer={debugCustomer} onClose={() => setDebugCustomer(null)} />}
    </div>
  );
}
